import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

interface ZoneOption {
  zone: string;
  /** e.g. "India Standard Time" — falls back to the zone name. */
  name: string;
  /** e.g. "GMT+05:30". */
  offset: string;
  /** Cities shown alongside the zone, when we have any. */
  cities: string[];
  /** Lowercased haystack of every searchable part. */
  haystack: string;
}

/**
 * Cities people actually search for, mapped to the zone that governs them.
 *
 * IANA names a zone after one representative city, so "Houston" and "Bengaluru"
 * are unfindable without this. Chevron sites come first in each list; the rest
 * are major cities included so the picker is useful generally. A zone missing
 * from here is still selectable by its IANA name or long name — an absent entry
 * degrades search, it never blocks a selection.
 */
const ZONE_CITIES: Record<string, string[]> = {
  // ── Americas ──────────────────────────────────────────────────────────────
  'America/Chicago': ['Houston', 'Dallas', 'Midland', 'Covington', 'New Orleans', 'Pascagoula', 'Austin', 'Chicago'],
  'America/Los_Angeles': ['San Ramon', 'El Segundo', 'Richmond', 'Bakersfield', 'San Francisco', 'Los Angeles', 'Seattle'],
  'America/Denver': ['Denver', 'Salt Lake City'],
  'America/New_York': ['New York', 'Washington', 'Boston', 'Atlanta', 'Miami'],
  'America/Anchorage': ['Anchorage'],
  'America/Edmonton': ['Calgary', 'Edmonton'],
  'America/Toronto': ['Toronto', 'Montreal', 'Ottawa'],
  'America/Vancouver': ['Vancouver'],
  'America/Mexico_City': ['Mexico City'],
  'America/Bogota': ['Bogota'],
  'America/Caracas': ['Caracas'],
  'America/Lima': ['Lima'],
  'America/Santiago': ['Santiago'],
  'America/Sao_Paulo': ['Sao Paulo', 'Rio de Janeiro', 'Brasilia'],
  'America/Argentina/Buenos_Aires': ['Buenos Aires'],
  'America/Port_of_Spain': ['Port of Spain'],

  // ── Europe ────────────────────────────────────────────────────────────────
  'Europe/London': ['Aberdeen', 'London', 'Edinburgh', 'Glasgow'],
  'Europe/Dublin': ['Dublin'],
  'Europe/Amsterdam': ['The Hague', 'Amsterdam', 'Rotterdam'],
  'Europe/Oslo': ['Stavanger', 'Oslo', 'Bergen'],
  'Europe/Copenhagen': ['Copenhagen'],
  'Europe/Stockholm': ['Stockholm'],
  'Europe/Paris': ['Paris'],
  'Europe/Berlin': ['Berlin', 'Frankfurt', 'Munich', 'Hamburg'],
  'Europe/Brussels': ['Brussels'],
  'Europe/Zurich': ['Zurich', 'Geneva'],
  'Europe/Madrid': ['Madrid', 'Barcelona'],
  'Europe/Lisbon': ['Lisbon'],
  'Europe/Rome': ['Rome', 'Milan'],
  'Europe/Vienna': ['Vienna'],
  'Europe/Warsaw': ['Warsaw'],
  'Europe/Kyiv': ['Kyiv'],
  'Europe/Istanbul': ['Istanbul', 'Ankara'],
  'Europe/Moscow': ['Moscow', 'Saint Petersburg'],

  // ── Africa ────────────────────────────────────────────────────────────────
  'Africa/Lagos': ['Lagos', 'Warri', 'Escravos', 'Port Harcourt', 'Abuja'],
  'Africa/Luanda': ['Luanda', 'Cabinda'],
  'Africa/Malabo': ['Malabo'],
  'Africa/Brazzaville': ['Pointe-Noire', 'Brazzaville'],
  'Africa/Kinshasa': ['Kinshasa'],
  'Africa/Accra': ['Accra'],
  'Africa/Abidjan': ['Abidjan'],
  'Africa/Cairo': ['Cairo', 'Alexandria'],
  'Africa/Tripoli': ['Tripoli'],
  'Africa/Algiers': ['Algiers'],
  'Africa/Casablanca': ['Casablanca', 'Rabat'],
  'Africa/Nairobi': ['Nairobi'],
  'Africa/Johannesburg': ['Johannesburg', 'Cape Town', 'Pretoria'],

  // ── Middle East & Central Asia ────────────────────────────────────────────
  'Asia/Riyadh': ['Riyadh', 'Al-Khafji', 'Jeddah', 'Dhahran'],
  'Asia/Kuwait': ['Kuwait City'],
  'Asia/Qatar': ['Doha'],
  'Asia/Bahrain': ['Manama'],
  'Asia/Dubai': ['Dubai', 'Abu Dhabi'],
  'Asia/Muscat': ['Muscat'],
  'Asia/Baghdad': ['Baghdad', 'Basra'],
  'Asia/Tehran': ['Tehran'],
  'Asia/Jerusalem': ['Tel Aviv', 'Jerusalem', 'Haifa'],
  'Asia/Atyrau': ['Atyrau', 'Tengiz'],
  'Asia/Almaty': ['Almaty', 'Astana'],
  'Asia/Tashkent': ['Tashkent'],
  'Asia/Ashgabat': ['Ashgabat'],
  'Asia/Baku': ['Baku'],
  'Asia/Tbilisi': ['Tbilisi'],

  // ── South & East Asia, Oceania ────────────────────────────────────────────
  'Asia/Kolkata': ['Bengaluru', 'Bangalore', 'Mumbai', 'New Delhi', 'Chennai', 'Hyderabad', 'Pune', 'Kolkata'],
  'Asia/Karachi': ['Karachi', 'Islamabad', 'Lahore'],
  'Asia/Dhaka': ['Dhaka'],
  'Asia/Yangon': ['Yangon'],
  'Asia/Bangkok': ['Bangkok'],
  'Asia/Ho_Chi_Minh': ['Ho Chi Minh City', 'Hanoi'],
  'Asia/Singapore': ['Singapore'],
  'Asia/Kuala_Lumpur': ['Kuala Lumpur'],
  'Asia/Jakarta': ['Jakarta'],
  'Asia/Makassar': ['Balikpapan', 'Makassar'],
  'Asia/Manila': ['Manila'],
  'Asia/Hong_Kong': ['Hong Kong'],
  'Asia/Taipei': ['Taipei'],
  'Asia/Shanghai': ['Beijing', 'Shanghai', 'Shenzhen', 'Guangzhou'],
  'Asia/Seoul': ['Seoul'],
  'Asia/Tokyo': ['Tokyo', 'Osaka'],
  'Australia/Perth': ['Perth'],
  'Australia/Darwin': ['Darwin'],
  'Australia/Adelaide': ['Adelaide'],
  'Australia/Brisbane': ['Brisbane'],
  'Australia/Sydney': ['Sydney', 'Canberra'],
  'Australia/Melbourne': ['Melbourne'],
  'Pacific/Auckland': ['Auckland', 'Wellington'],
  'Pacific/Port_Moresby': ['Port Moresby'],
};

/**
 * Deprecated zone names browsers still report (Windows commonly returns
 * "Asia/Calcutta"), pointed at the canonical name so they inherit its cities.
 */
const ZONE_EQUIVALENTS: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Europe/Kiev': 'Europe/Kyiv',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'Atlantic/Reykjavik': 'Atlantic/Reykjavik',
};

/**
 * A zone's UTC offset at a given moment, e.g. "GMT+05:30".
 *
 * Takes a date because the answer moves: zones observing daylight saving shift
 * by an hour twice a year, so the offset is only meaningful for an instant.
 */
export function zoneOffsetLabel(zone: string, at: Date = new Date()): string {
  const part = namePart(zone, at, 'shortOffset') || 'GMT';
  // The browser emits "GMT+5:30" and a bare "GMT" for UTC; normalise both.
  const match = /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/.exec(part);
  if (!match) {
    return 'GMT+00:00';
  }
  const [, sign, hours, minutes] = match;
  return `GMT${sign}${hours.padStart(2, '0')}:${minutes ?? '00'}`;
}

function namePart(zone: string, at: Date, timeZoneName: 'long' | 'shortOffset'): string {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName })
        .formatToParts(at)
        .find((p) => p.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
}

/** Searchable single-select of IANA time zones, sourced from the browser. */
@Component({
  selector: 'app-timezone-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './timezone-picker.component.html',
  styleUrls: ['./timezone-picker.component.css'],
})
export class TimezonePickerComponent {
  @Input() selected = '';
  @Input() placeholder = 'Select a time zone…';
  @Output() selectedChange = new EventEmitter<string>();

  open = false;
  filter = '';

  readonly options: ZoneOption[] = TimezonePickerComponent.buildOptions();

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }

  toggleOpen(): void {
    this.open = !this.open;
    if (this.open) {
      this.filter = '';
    }
  }

  get filteredOptions(): ZoneOption[] {
    const f = this.filter.trim().toLowerCase();
    if (!f) {
      return this.options;
    }
    return this.options.filter((o) => o.haystack.includes(f));
  }

  get selectedLabel(): string {
    const match = this.options.find((o) => o.zone === this.selected);
    return match ? this.label(match) : '';
  }

  label(option: ZoneOption): string {
    const base =
      option.name === option.zone
        ? `(${option.offset}) ${option.zone}`
        : `(${option.offset}) ${option.name} — ${option.zone}`;
    return option.cities.length ? `${base} · ${option.cities.slice(0, 3).join(', ')}` : base;
  }

  isSelected(option: ZoneOption): boolean {
    return option.zone === this.selected;
  }

  choose(option: ZoneOption): void {
    this.open = false;
    this.selectedChange.emit(option.zone);
  }

  // ── Zone list, built once from the browser's own IANA data ─────────────────
  /** The browser ships (and updates) the IANA database, so this needs no upkeep. */
  private static buildOptions(): ZoneOption[] {
    const now = new Date();
    return TimezonePickerComponent.supportedZones()
      .map((zone) => {
        const name = TimezonePickerComponent.zoneName(zone, now);
        const offset = zoneOffsetLabel(zone, now);
        const cities = ZONE_CITIES[zone] ?? ZONE_CITIES[ZONE_EQUIVALENTS[zone]] ?? [];
        return {
          zone,
          name,
          offset,
          cities,
          haystack: `${zone} ${name} ${offset} ${cities.join(' ')}`
            .toLowerCase()
            .replace(/_/g, ' '),
        };
      })
      .sort((a, b) => a.zone.localeCompare(b.zone));
  }

  private static supportedZones(): string[] {
    const supported = (Intl as any).supportedValuesOf;
    if (typeof supported === 'function') {
      try {
        return supported.call(Intl, 'timeZone') as string[];
      } catch {
        // Fall through to the minimal list below.
      }
    }
    // Pre-2022 browsers: at least offer the caller's own zone plus UTC.
    const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return Array.from(new Set([local, 'UTC'].filter(Boolean)));
  }

  /** "India Standard Time" — or the zone name when the browser has no long form. */
  private static zoneName(zone: string, at: Date): string {
    const part = namePart(zone, at, 'long');
    // Browsers fall back to "GMT+5:30" for zones with no descriptive name.
    return !part || part.startsWith('GMT') ? zone : part;
  }
}
